import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { useElementDetail } from '../hooks/useElementDetail'
import type { GraphEdgeDto } from '../hooks/useGraph'

interface DetailPanelProps {
  elementKey: string
  edges: GraphEdgeDto[]
  canDrill: boolean
  onDrill: () => void
  onClose: () => void
  /** Pivot to the deployment lens at the node where this element runs. */
  onPivot?: ((deploymentNodeKey: string) => void) | undefined
}

/** Side panel for the selected element: structure + status + instances +
 *  the relationships touching it (UX charter R10 object-page-ish). */
export function DetailPanel({ elementKey, edges, canDrill, onDrill, onClose, onPivot }: DetailPanelProps) {
  const { t } = useTranslation()
  const { data, isLoading } = useElementDetail(elementKey)
  const rels = edges.filter((e) => e.from === elementKey || e.to === elementKey)

  return (
    <aside className="w-80 shrink-0 rounded border border-neutral-200 p-4 dark:border-neutral-800">
      <div className="flex items-start justify-between">
        <h3 className="font-semibold">{data?.name ?? elementKey}</h3>
        <button type="button" aria-label={t('detailPanel.close')} onClick={onClose} className="text-neutral-400">
          <X size={16} />
        </button>
      </div>

      {isLoading && <p className="mt-2 text-sm text-neutral-400">…</p>}
      {data && (
        <dl className="mt-3 grid grid-cols-[6rem_1fr] gap-y-1 text-xs">
          <dt className="text-neutral-500">{t('detailPanel.kind')}</dt><dd>{data.kind}</dd>
          <dt className="text-neutral-500">{t('detailPanel.health')}</dt><dd>{data.health}</dd>
          {data.technology && (<><dt className="text-neutral-500">{t('detailPanel.tech')}</dt><dd>{data.technology}</dd></>)}
          {data.tags.length > 0 && (<><dt className="text-neutral-500">{t('detailPanel.tags')}</dt><dd>{data.tags.join(', ')}</dd></>)}
        </dl>
      )}

      {data && data.instances.length > 0 && (
        <div className="mt-3">
          <h4 className="text-xs font-semibold uppercase text-neutral-500">{t('detailPanel.instances')}</h4>
          <ul className="mt-1 text-xs">
            {data.instances.map((i, idx) => (
              <li key={idx}>{i.deploymentNodeKey} · {i.environment}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3">
        <h4 className="text-xs font-semibold uppercase text-neutral-500">{t('detailPanel.relationships')}</h4>
        {rels.length === 0 ? (
          <p className="mt-1 text-xs text-neutral-400">{t('detailPanel.none')}</p>
        ) : (
          <ul className="mt-1 text-xs">
            {rels.map((r, idx) => (
              <li key={idx}>{r.from} → {r.to}{r.description ? ` (${r.description})` : ''}</li>
            ))}
          </ul>
        )}
      </div>

      {canDrill && (
        <button type="button" onClick={onDrill} className="mt-4 w-full rounded bg-neutral-900 px-3 py-2 text-sm text-white dark:bg-neutral-100 dark:text-neutral-900">
          {t('detailPanel.drillIn')}
        </button>
      )}
      {onPivot && data && data.instances.length > 0 && data.instances[0] && (
        <button
          type="button"
          onClick={() => onPivot(data.instances[0]!.deploymentNodeKey)}
          className="mt-2 w-full rounded border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700"
        >
          {t('detailPanel.viewDeployment')}
        </button>
      )}
    </aside>
  )
}
